import { Grid, Box, FormControlLabel, Checkbox, FormHelperText, FormControl, styled } from '@ori-ui/mui';
import type { GlobalSharedMetadataValidationStringValidationDto } from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FocusEventHandler, FC, ChangeEvent, ReactElement } from 'react';
import { Controller } from 'react-hook-form';

import { renderValidationMessage } from '../../../utils';

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  alignItems: 'start',
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  paddingTop: theme.spacing(0),
}));

export interface ICheckboxFormFieldByConfigProps {
  name: string;
  label?: ReactElement | string;
  isAvailable?: boolean;
  displayOrder?: number;
  validation?: GlobalSharedMetadataValidationStringValidationDto;
  defaultValue?: boolean;
  value?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const customRequiredFields = new Set(['confirmation1', 'confirmation2', 'termsAndConditions']);

export const CheckboxFormFieldByConfig: FC<ICheckboxFormFieldByConfigProps> = (props) => {
  const { name, label, isAvailable, displayOrder, defaultValue, validation, value, onChange } = props;

  const { getTestIdProps } = useTestId();

  const customRequired = customRequiredFields.has(name);

  const renderField = () => (
    <Grid
      item
      xs={12}
      style={{ order: displayOrder }}
    >
      <Box
        minWidth={100}
        marginBottom={3}
        textAlign="left"
      >
        <Controller
          name={name}
          rules={{
            required: validation?.isMandatory,
          }}
          render={({ field, fieldState }) => (
            <FormControl
              error={!!fieldState.error}
              component="fieldset"
            >
              <StyledFormControlLabel
                control={
                  <StyledCheckbox
                    color="primary"
                    required={customRequired ? false : validation?.isMandatory}
                    defaultChecked={defaultValue}
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- we need logical operator ||
                    checked={value || (field.value as boolean)}
                    value={field.value as boolean}
                    inputRef={field.ref}
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- we need logical operator ||
                    onChange={onChange || field.onChange}
                    {...getTestIdProps(`${name}-checkbox-field`)}
                  />
                }
                label={label ?? ''}
                id={name}
              />
              {fieldState.error ? <FormHelperText>{renderValidationMessage(fieldState.error)}</FormHelperText> : null}
            </FormControl>
          )}
        />
      </Box>
    </Grid>
  );

  const fieldComponent = isAvailable && renderField();

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment -- we need fragment
    <>{fieldComponent}</>
  );
};
